import { useEffect, useRef, useState } from "react";
import { Search, Loader2, X, Copy, Plus, Package } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Stepper } from "@/components/wholesale/stepper";
import { useCatalog, useStoreFilter, useWholesaleContext } from "@/hooks/use-wholesale";
import { useDebounce } from "@/hooks/use-debounce";
import { fmtKm } from "@/lib/wholesale-format";
import { cn } from "@/lib/utils";

export type CatalogHit = {
  storeId: string; storeName: string; wcProductId: number; wcVariationId: number | null;
  sku: string; name: string; imageUrl: string; listPrice: number | null; stockQuantity: number | null;
  stockStatus?: string;
};

const ALL = "__all__";

/** Na telefonu `autoFocus` odmah digne tastaturu i pojede pola ekrana s rezultatima,
 *  pa se fokus daje samo na širim ekranima gdje tastatura ništa ne prekriva. */
function autoFocusOnWideScreen(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(min-width: 640px)").matches;
}

function stockLabel(qty: number | null | undefined): string {
  if (qty === null || qty === undefined) return "—";
  return `${qty} kom`;
}

function hitKey(it: CatalogHit): string {
  return `${it.storeId}:${it.wcProductId}:${it.wcVariationId ?? 0}`;
}

export function CatalogSearch({
  onPick,
  autoFocus = true,
  placeholder = "Pretraži po nazivu ili SKU…",
  action = "add",
  quantityOf,
  onSetQuantity,
}: {
  onPick: (item: CatalogHit) => void;
  autoFocus?: boolean;
  placeholder?: string;
  /** Šta radi odabir stavke — određuje ikonu akcije i tekst tastaturne upute. */
  action?: "add" | "copy";
  /** Količina pogotka u narudžbi. Uz `onSetQuantity` svaki red dobija stepper umjesto ikone akcije. */
  quantityOf?: (item: CatalogHit) => number;
  onSetQuantity?: (item: CatalogHit, quantity: number) => void;
}) {
  const [q, setQ] = useState("");
  const { store, setStore } = useStoreFilter(ALL);
  const debounced = useDebounce(q);
  const ctx = useWholesaleContext();
  const sources = ctx.data?.sources ?? [];
  const { data, isFetching, error } = useCatalog(debounced, store === ALL ? "" : store);
  const hits = data ?? [];
  const tooShort = debounced.trim().length < 2;
  const ActionIcon = action === "copy" ? Copy : Plus;
  const withStepper = !!(quantityOf && onSetQuantity);
  // Stepper ima svoja dugmad, a dugme u dugmetu nije validan HTML — zato je red tada `div`.
  const Row = withStepper ? "div" : "button";

  // Tastaturna navigacija: strelice biraju red, Enter ga potvrđuje, a fokus cijelo
  // vrijeme ostaje u polju za pretragu da se upit može dopisivati bez miša.
  const [active, setActive] = useState(0);
  const rowsRef = useRef<Map<string, HTMLElement>>(new Map());
  useEffect(() => setActive(0), [debounced, store]);
  useEffect(() => {
    const it = hits[active];
    if (it) rowsRef.current.get(hitKey(it))?.scrollIntoView({ block: "nearest" });
  }, [active, hits]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (hits.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % hits.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i - 1 + hits.length) % hits.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const it = hits[active];
      if (it) onPick(it);
    }
  }

  return (
    <div className="flex flex-col min-h-0">
      <div className="sticky top-0 z-10 bg-background flex flex-col gap-2 pt-2 -mt-2 pb-2 sm:pb-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1 sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:left-2.5 sm:h-3.5 sm:w-3.5 text-muted-foreground" />
            <Input
              className="h-11 text-base pl-10 pr-10 sm:h-8 sm:text-xs sm:pl-8 sm:pr-8"
              placeholder={placeholder}
              value={q}
              autoFocus={autoFocus && autoFocusOnWideScreen()}
              inputMode="search"
              enterKeyHint="search"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            {isFetching ? (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:right-2.5 sm:h-3.5 sm:w-3.5 animate-spin text-muted-foreground" />
            ) : q ? (
              <button
                type="button"
                onClick={() => setQ("")}
                aria-label="Očisti pretragu"
                className="absolute right-0 top-0 h-full w-10 sm:w-8 flex items-center justify-center text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
              </button>
            ) : null}
          </div>
          {sources.length > 0 && (
            <Select value={store} onValueChange={setStore}>
              <SelectTrigger className="w-40 shrink-0 hidden sm:flex">
                <SelectValue placeholder="Svi shopovi" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Svi shopovi</SelectItem>
                {sources.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {!tooShort && hits.length > 0 && (
            <span className="hidden sm:block ml-auto text-[10px] uppercase tracking-[0.08em] text-muted-foreground tabular-nums">
              {hits.length} {hits.length === 1 ? "rezultat" : "rezultata"}
            </span>
          )}
        </div>

        {/* Na telefonu filter shopa je red čipova — jedan dodir umjesto otvaranja padajuće liste. */}
        {sources.length > 1 && (
          <div className="flex sm:hidden items-center gap-1.5 overflow-x-auto -mx-4 px-4 pb-0.5">
            {[{ id: ALL, name: "Svi shopovi" }, ...sources].map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setStore(s.id)}
                className={cn(
                  "shrink-0 h-8 px-3 text-xs border transition-colors",
                  store === s.id
                    ? "bg-foreground text-background border-foreground font-medium"
                    : "border-input text-muted-foreground",
                )}
              >
                {s.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {error && <p className="text-destructive text-xs py-2">{(error as Error).message}</p>}

      {tooShort ? (
        <p className="text-xs text-muted-foreground py-4 sm:py-10 sm:px-8 sm:ring-1 sm:ring-foreground/10 sm:text-center">
          Upiši bar dva znaka — traži se po nazivu i SKU-u.
        </p>
      ) : isFetching && hits.length === 0 ? (
        <div className="flex flex-col gap-1.5">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 sm:h-11 w-full" />)}
        </div>
      ) : hits.length === 0 ? (
        <p className="text-xs text-muted-foreground py-4 sm:py-10 sm:px-8 sm:ring-1 sm:ring-foreground/10 sm:text-center">
          Nema rezultata za „{debounced.trim()}". Provjeri SKU ili probaj kraći pojam.
        </p>
      ) : (
        <div className="ring-1 ring-foreground/10 flex flex-col min-h-0">
          {/* Zaglavlje kolona postoji samo na širem ekranu, gdje red i jeste tabelaran. */}
          <div className="hidden sm:flex items-center gap-2.5 px-2 py-1.5 border-b bg-muted/50 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            <span className="w-10 shrink-0" />
            <span className="flex-1">Artikal</span>
            <span className="w-28 shrink-0">Shop</span>
            <span className="w-16 text-right shrink-0">Zaliha</span>
            <span className="w-24 text-right shrink-0">Cijena</span>
            {withStepper ? <span className="w-[5.75rem] text-center shrink-0">Količina</span> : <span className="w-4 shrink-0" />}
          </div>

          <div className="flex flex-col divide-y overflow-y-auto">
            {hits.map((it: CatalogHit, i: number) => {
              const qty = quantityOf?.(it) ?? 0;
              return (
                <Row
                  key={hitKey(it)}
                  ref={(el: HTMLElement | null) => {
                    if (el) rowsRef.current.set(hitKey(it), el);
                    else rowsRef.current.delete(hitKey(it));
                  }}
                  type={withStepper ? undefined : "button"}
                  onClick={() => onPick(it)}
                  onMouseEnter={() => setActive(i)}
                  className={cn(
                    "group flex items-center gap-3 p-3 sm:gap-2.5 sm:px-2 sm:py-1.5 text-left text-xs transition-colors active:bg-muted",
                    i === active && "sm:bg-muted/60",
                    withStepper && "cursor-pointer",
                  )}
                >
                  <span className="relative flex shrink-0">
                    {it.imageUrl ? (
                      <img
                        src={it.imageUrl}
                        alt=""
                        className="h-14 w-14 sm:h-10 sm:w-10 object-cover shrink-0 ring-1 ring-foreground/10"
                        loading="lazy"
                      />
                    ) : (
                      <div className="h-14 w-14 sm:h-10 sm:w-10 shrink-0 bg-muted ring-1 ring-foreground/10 flex items-center justify-center">
                        <Package className="h-5 w-5 sm:h-4 sm:w-4 text-muted-foreground/50" />
                      </div>
                    )}
                    {qty > 0 && (
                      <span className="absolute left-0 top-0 flex h-5 min-w-5 items-center justify-center bg-foreground px-1 text-[11px] font-semibold tabular-nums text-background">
                        {qty}
                      </span>
                    )}
                  </span>
  
                  <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                    <span className="text-sm sm:text-xs font-medium leading-snug line-clamp-2 sm:line-clamp-none sm:truncate">
                      {it.name || "Bez naziva"}
                    </span>
                    <span className="text-xs text-muted-foreground tabular-nums truncate">
                      <span className={cn("font-bold tracking-wide", it.sku ? "text-foreground" : "font-normal")}>
                        {it.sku || "bez SKU"}
                      </span>
                      <span className="sm:hidden"> · {it.storeName}</span>
                    </span>
                    {/* Cijena i zaliha stoje uz naziv na telefonu jer desnih kolona nema. */}
                    <span className="flex sm:hidden items-baseline gap-2 pt-0.5">
                      <span className="text-sm font-semibold tabular-nums">{fmtKm(it.listPrice)}</span>
                      <span
                        className={cn(
                          "text-[11px] tabular-nums",
                          (it.stockQuantity ?? 0) <= 0 ? "text-destructive" : "text-muted-foreground",
                        )}
                      >
                        {stockLabel(it.stockQuantity)}
                      </span>
                    </span>
                  </div>
  
                  <span className="hidden sm:block w-28 shrink-0">
                    <Badge variant="outline">{it.storeName}</Badge>
                  </span>
                  <span
                    className={cn(
                      "hidden sm:block w-16 text-right tabular-nums shrink-0",
                      (it.stockQuantity ?? 0) <= 0 ? "text-destructive" : "text-muted-foreground",
                    )}
                  >
                    {stockLabel(it.stockQuantity)}
                  </span>
                  <span className="hidden sm:block w-24 text-right tabular-nums font-medium shrink-0">
                    {fmtKm(it.listPrice)}
                  </span>
  
                  {withStepper ? (
                    // Klik na stepper mijenja samo količinu — ne smije se proslijediti redu kao još +1.
                    <div className="shrink-0 w-[5.75rem] flex justify-center" onClick={(e) => e.stopPropagation()}>
                      <Stepper size="sm" value={qty} onChange={(n) => onSetQuantity!(it, n)} label={it.name} />
                    </div>
                  ) : (
                    <ActionIcon
                      className={cn(
                        "h-4 w-4 shrink-0 text-muted-foreground sm:h-3.5 sm:w-3.5 sm:opacity-0 sm:transition-opacity group-hover:sm:opacity-100",
                        i === active && "sm:opacity-100",
                      )}
                    />
                  )}
                </Row>
              );
            })}
          </div>

          <p className="hidden sm:flex items-center gap-1.5 border-t px-2 py-1.5 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            <kbd className="border px-1 py-px font-sans">↑</kbd>
            <kbd className="border px-1 py-px font-sans">↓</kbd>
            kretanje
            <span className="text-muted-foreground/40">·</span>
            <kbd className="border px-1 py-px font-sans">⏎</kbd>
            {action === "copy" ? "kopiraj SKU" : "dodaj artikal"}
          </p>
        </div>
      )}
    </div>
  );
}
